import { useLocation } from "react-router-dom";
import { Search, Bell } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/auth/useAuth";

const routeLabels: Record<string, string> = {
  "/": "Dashboard",
  "/vms": "Virtual Machines",
  "/vms/trash": "Trash",
  "/docker": "Docker",
  "/provisioning": "Provisioning",
  "/backups": "Backups",
  "/monitoring": "Monitoring",
  "/ipam": "IPAM",
  "/settings": "Settings",
  "/admin/reconciliation": "Reconciliation",
};

function getBreadcrumb(pathname: string): { label: string; segments: string[] } {
  // Exact match first so /vms/trash doesn't fall into the numeric VM
  // detail branch below.
  if (pathname in routeLabels) {
    const label = routeLabels[pathname];
    return { label, segments: [label] };
  }

  // VM detail pages: /vms/<numeric-id>
  if (/^\/vms\/\d+$/.test(pathname)) {
    const vmId = pathname.split("/")[2];
    return {
      label: `VM #${vmId}`,
      segments: ["Virtual Machines", `VM #${vmId}`],
    };
  }

  return { label: "Page", segments: ["Page"] };
}

export function Header() {
  const location = useLocation();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const breadcrumb = getBreadcrumb(location.pathname);

  return (
    <header className="flex h-16 items-center justify-between border-b border-gray-800 bg-gray-900/50 px-6 backdrop-blur-sm">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <nav className="flex items-center gap-1 text-sm">
          <span className="text-gray-500">AlphaProx</span>
          {breadcrumb.segments.map((segment, idx) => (
            <span key={idx} className="flex items-center gap-1">
              <span className="text-gray-600">/</span>
              <span
                className={
                  idx === breadcrumb.segments.length - 1
                    ? "font-medium text-gray-200"
                    : "text-gray-400"
                }
              >
                {segment}
              </span>
            </span>
          ))}
        </nav>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search VMs, IPs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-64 rounded-lg border border-gray-700 bg-gray-800 py-2 pl-10 pr-4 text-sm text-gray-200 placeholder-gray-500 outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Notifications */}
        <button className="relative rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-200">
          <Bell className="h-5 w-5" />
          <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500" />
        </button>

        {/* User Avatar */}
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-xs font-medium text-white">
          {user?.firstName?.charAt(0) ?? ""}
          {user?.lastName?.charAt(0) ?? ""}
        </div>
      </div>
    </header>
  );
}
